import { useState } from 'react';
import { ArrowLeft, ArrowRight, Calendar, Zap, Bell, Film, Image, Sparkles, Clock, Smartphone, Lock } from 'lucide-react';
import './LandingPage.css';

const SW_PASSCODE = 'future2026';

export default function SocialWeekPage({ onBack, onDemo }) {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('sw_info_unlocked') === '1');
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  const tryUnlock = (e) => {
    e.preventDefault();
    if (code.trim().toLowerCase() === SW_PASSCODE) {
      sessionStorage.setItem('sw_info_unlocked', '1');
      setUnlocked(true);
    } else {
      setError(true);
    }
  };

  if (!unlocked) {
    return (
      <div className="lp" data-testid="socialweek-passcode-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0d1f17 0%, #14352a 60%, #1a4534 100%)' }}>
        <form onSubmit={tryUnlock} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(16px)', borderRadius: 20, padding: '48px 40px', maxWidth: 420, width: '90%', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Lock size={26} color="#f59e0b" />
          </div>
          <h1 style={{ color: '#fff', fontSize: 26, margin: '0 0 8px', fontWeight: 700 }}>Social Week</h1>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, margin: '0 0 28px' }}>This page is invite only. Enter the passcode to continue.</p>
          <input
            type="password"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(false); }}
            placeholder="Enter passcode"
            autoFocus
            data-testid="sw-passcode-input"
            style={{ width: '100%', boxSizing: 'border-box', padding: '13px 16px', borderRadius: 12, border: error ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: 15, outline: 'none', textAlign: 'center', letterSpacing: 2 }}
          />
          {error && <p data-testid="sw-passcode-error" style={{ color: '#f87171', fontSize: 13, margin: '10px 0 0' }}>Incorrect passcode. Try again.</p>}
          <button type="submit" data-testid="sw-passcode-submit" style={{ width: '100%', marginTop: 16, padding: '13px 16px', borderRadius: 12, border: 'none', background: '#f59e0b', color: '#1a1a1a', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
            Unlock <ArrowRight size={14} style={{ verticalAlign: -2 }} />
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="lp" data-testid="socialweek-page">
      <div className="sp-hero sp-hero-amber" data-testid="sp-hero">
        <button className="sp-back sp-back-right" onClick={onBack} data-testid="sp-back">
          Back to NicheSocial <ArrowRight size={16} />
        </button>
        <div className="sp-hero-inner">
          <div className="sp-hero-content">
            <span className="sp-badge sp-badge-amber">Early Access</span>
            <h1 className="sp-title">Social Week</h1>
            <p className="sp-subtitle">
              A weekly social content planner built for estate agents. Instant access to your listings,
              auto-generated content, AI captions, and push notification reminders. All in one place.
            </p>
            <p className="sp-body">
              No more WeTransfer. No more downloading files to a computer and moving to mobile.
              Every video and image created by our team is available directly in the app, ready to post.
            </p>
            <button className="sp-cta sp-cta-dark" onClick={onDemo} data-testid="sp-cta">
              Book a Demo <ArrowRight size={16} />
            </button>
          </div>
          <div className="sp-hero-visual">
            <div className="sp-icon-ring sp-icon-ring-amber">
              <Calendar size={48} />
            </div>
          </div>
        </div>
      </div>

      <section className="sp-section sp-section-white" data-testid="sp-how">
        <div className="sp-container">
          <h2 className="sp-h2 sp-h2-amber">How it works</h2>
          <div className="sp-steps-row">
            <div className="sp-step-card" data-testid="sp-step-1">
              <span className="sp-step-num sp-step-amber">01</span>
              <h3>We shoot your property</h3>
              <p>Our photographers capture photography, video, and all the assets from a single visit. Same process you already know and trust.</p>
            </div>
            <div className="sp-step-card" data-testid="sp-step-2">
              <span className="sp-step-num sp-step-amber">02</span>
              <h3>Content is generated</h3>
              <p>Social ready assets are created automatically. Sneak peek videos, lifestyle images, branded posts, and AI captions using your Social Voice settings.</p>
            </div>
            <div className="sp-step-card" data-testid="sp-step-3">
              <span className="sp-step-num sp-step-amber">03</span>
              <h3>Plan your week</h3>
              <p>Drag content into your weekly schedule. Mix property posts with lifestyle images, videos, and market updates for a balanced social presence.</p>
            </div>
            <div className="sp-step-card" data-testid="sp-step-4">
              <span className="sp-step-num sp-step-amber">04</span>
              <h3>Get reminded to post</h3>
              <p>Our app sends push notifications to your agents at the scheduled time. They pick and choose what to publish, keeping full control over their channels.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="sp-section sp-section-grey" data-testid="sp-features">
        <div className="sp-container">
          <h2 className="sp-h2">Why agents love Social Week</h2>
          <div className="sp-grid">
            <div className="sp-card" data-testid="sp-card-instant">
              <Zap size={20} className="sp-card-icon sp-icon-amber" />
              <h3>Instant Access to Listings</h3>
              <p>Every property shot by our team appears instantly in Social Week. No file transfers, no waiting, no hunting through emails.</p>
            </div>
            <div className="sp-card" data-testid="sp-card-video">
              <Film size={20} className="sp-card-icon sp-icon-amber" />
              <h3>Videos Direct in App</h3>
              <p>All videos created by our team are available directly in Social Week. No more WeTransfer links, downloading to desktop, or moving files to mobile.</p>
            </div>
            <div className="sp-card" data-testid="sp-card-captions">
              <Sparkles size={20} className="sp-card-icon sp-icon-amber" />
              <h3>Social Voice and AI Captions</h3>
              <p>AI-generated captions that match your branch tone, emoji style, and spacing. Consistent messaging without the copywriting effort.</p>
            </div>
            <div className="sp-card" data-testid="sp-card-notify">
              <Bell size={20} className="sp-card-icon sp-icon-amber" />
              <h3>Push Notification Reminders</h3>
              <p>Scheduled posts trigger push notifications to agents. They choose what to publish, avoiding the risk of low-quality automated posting.</p>
            </div>
            <div className="sp-card" data-testid="sp-card-images">
              <Image size={20} className="sp-card-icon sp-icon-amber" />
              <h3>Auto-Generated Content</h3>
              <p>Branded social images, lifestyle shots, and sneak peek videos are all generated from the assets we already capture. Zero extra effort.</p>
            </div>
            <div className="sp-card" data-testid="sp-card-flow">
              <Smartphone size={20} className="sp-card-icon sp-icon-amber" />
              <h3>Great Flow, Any Device</h3>
              <p>Plan on desktop, post from your phone. Social Week works as a PWA. Install it on your home screen and use it like a native app.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="sp-section sp-section-white" data-testid="sp-difference">
        <div className="sp-container">
          <h2 className="sp-h2">The Social Week difference</h2>
          <div className="sp-split">
            <div className="sp-split-col">
              <h3 className="sp-split-title sp-split-red">Without Social Week</h3>
              <ul className="sp-split-list sp-split-list-red">
                <li>WeTransfer links and email attachments</li>
                <li>Download to computer, transfer to phone</li>
                <li>Writing captions from scratch each time</li>
                <li>Inconsistent posting across branches</li>
                <li>Agents forget to post for days or weeks</li>
                <li>Marketing team chasing agents for content</li>
              </ul>
            </div>
            <div className="sp-split-col">
              <h3 className="sp-split-title sp-split-green">With Social Week</h3>
              <ul className="sp-split-list sp-split-list-green">
                <li>All content in one place, ready to use</li>
                <li>Videos and images direct in the app</li>
                <li>AI captions matching your brand voice</li>
                <li>Consistent weekly schedule for every branch</li>
                <li>Push reminders so agents never miss a post</li>
                <li>Agents choose what to publish with full control</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="sp-section sp-section-amber" data-testid="sp-access">
        <div className="sp-container sp-pricing-block">
          <h2 className="sp-h2 sp-h2-dark">Early Access</h2>
          <p className="sp-price-note sp-price-note-dark">
            Social Week Beta is included with Social Go and Social Pro packages.
            Get early access to the full weekly planning experience and help shape the features that matter most to your branch.
          </p>
          <button className="sp-cta sp-cta-dark" onClick={onDemo} data-testid="sp-access-cta">
            Book a Demo <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <footer className="lp-footer" data-testid="sp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <span className="lp-logo lp-logo-footer">
              <span className="lp-logo-niche">niche</span><span className="lp-logo-social">social</span>
            </span>
            <p className="lp-footer-powered">Powered by NicheCom property marketing assets.</p>
          </div>
          <nav className="lp-footer-links">
            <button onClick={onBack}>Back to NicheSocial</button>
          </nav>
        </div>
      </footer>
    </div>
  );
}
